/**
 * Simulation v2 enrichment layer (spec §4).
 *
 * Wraps the existing BFS executor (simulate) and enriches each step with:
 *   - cfKind (trigger | callable | scheduled | http)
 *   - actor lane ('cf-callable' for callable CFs, 'cf' for all others)
 *   - latency band (p50/p95/p99) from latencyModel
 *   - cumulativeLatencyMs (running sum of p95 across steps)
 *   - agentGate result (constitutional 14-principle evaluation)
 *
 * Returns EnrichedSimulationTrace. The BFS visited-set and MAX_HOPS cap
 * are unchanged from v1 — this layer is purely additive.
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import {
  simulate,
  type ScenarioAction,
  type EnrichedSimulationStep,
  type EnrichedSimulationTrace,
  type CoverageSnapshot,
  type SafetyTier,
} from './simulator.js';
import { estimateLatency } from './latencyModel.js';
import { getCfKind } from './cfKindRouter.js';
import { evaluateAgentGate } from './agentGate.js';
import { getSandboxOnlyEntities } from './sandboxUtils.js';

const EMPTY_COVERAGE: CoverageSnapshot = {
  entityHits: [],
  cfHits: [],
  domainCoverage: [],
  darkZones: [],
  totalEntityCoverage: 0,
};

function tierPriority(t: SafetyTier): number {
  return t === 'BLOCK' ? 2 : t === 'WARN' ? 1 : 0;
}

/**
 * Enriches a simulation trace with latency, CF kind, and agent gate data.
 *
 * @param action - The scenario action (ScenarioAction or legacy UserAction)
 * @param manifest - The Atlas manifest
 * @param autonomyLevel - 0–3; agent gate only evaluated when > 0
 * @param coverage - Optional pre-computed coverage (pass a CoverageTracker snapshot)
 * @param overlayManifest - Optional manifest overlay to enrich the simulation
 */
export function simulateV2(
  action: ScenarioAction,
  manifest: AtlasManifest,
  autonomyLevel = 0,
  coverage: CoverageSnapshot = EMPTY_COVERAGE,
  overlayManifest?: AtlasManifest,
): EnrichedSimulationTrace {
  const baseTrace = simulate(manifest, action, overlayManifest);

  // Compute sandbox-only entity set for marking steps
  const sandboxOnlyIds = overlayManifest ? getSandboxOnlyEntities(manifest, overlayManifest) : new Set<string>();

  let cumulativeLatencyMs = 0;
  let worstTier: SafetyTier = 'ALLOW';
  let totalP95 = 0;

  const enrichedSteps: EnrichedSimulationStep[] = baseTrace.steps.map((step) => {
    const cfName = step.actor === 'cf' ? step.source : undefined;
    const cfKind = getCfKind(cfName, manifest) ?? undefined;

    // Resolve actor lane: callable CFs get their own lane.
    const actor: EnrichedSimulationStep['actor'] =
      cfKind === 'callable' ? 'cf-callable'
      : step.actor === 'cf' ? 'cf'
      : step.actor;

    const latency = estimateLatency(actor, cfName);
    cumulativeLatencyMs += latency.p95;
    totalP95 += latency.p95;

    // Check if this step writes any sandbox-only entities
    const fromSandboxOverlay = step.writes.some((entityId) => sandboxOnlyIds.has(entityId)) || undefined;

    // Build a partial enriched step for agent gate evaluation.
    const partial: EnrichedSimulationStep = {
      ...step,
      actor,
      cfKind,
      latency,
      cumulativeLatencyMs,
      agentGate: undefined,
      fromSandboxOverlay,
    };

    const agentGate =
      autonomyLevel > 0
        ? partial.fromSandboxOverlay
          ? { tier: 'WARN' as const, reason: 'writes draft entity, not yet published to manifest', principleViolations: [] }
          : evaluateAgentGate(partial, manifest)
        : undefined;

    if (agentGate && tierPriority(agentGate.tier) > tierPriority(worstTier)) {
      worstTier = agentGate.tier;
    }

    return { ...partial, agentGate };
  });

  return {
    action,
    steps: enrichedSteps,
    coverage,
    totalLatencyMs: totalP95,
    safetyTier: worstTier,
  };
}
